import { Binoculars } from "@phosphor-icons/react"
import {
  Cell,
  HeaderCell,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "@tokenterminal/core/ui/table/Table"
import Image from "next/image"
import { Fragment } from "react"
import { BASE_PATH } from "@/constants/app.mjs"
import { PRODUCTS } from "@/constants/urls"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"

const mockFinancialData = {
  project: {
    name: "Ethereum",
    symbol: "ETH",
  },
  periods: ["Jan 2026", "Dec 2025", "Nov 2025", "Oct 2025"],
  sections: [
    {
      name: "Income statement",
      metrics: [
        {
          id: "fees",
          label: "Fees",
          values: [312400000, 298100000, 287600000, 275200000],
          changes: [0.048, -0.021, 0.045, 0.032],
        },
        {
          id: "revenue",
          label: "Revenue",
          values: [312400000, 298100000, 287600000, 275200000],
          changes: [0.048, -0.021, 0.045, 0.032],
        },
        {
          id: "expenses",
          label: "(Expenses)",
          values: [45200000, 42800000, 41300000, 39800000],
          changes: [0.056, 0.036, 0.038, 0.029],
          isExpense: true,
        },
        {
          id: "earnings",
          label: "Earnings",
          values: [267200000, 255300000, 246300000, 235400000],
          changes: [0.047, -0.025, 0.046, 0.034],
          isEarnings: true,
        },
      ],
    },
    {
      name: "Market data",
      metrics: [
        {
          id: "price",
          label: "Price",
          values: [3421, 3298, 3156, 3089],
          changes: [0.037, 0.045, 0.022, -0.012],
        },
        {
          id: "market_cap",
          label: "Market cap (circulating)",
          values: [411200000000, 396500000000, 379400000000, 371200000000],
          changes: [0.037, 0.045, 0.022, -0.012],
        },
      ],
    },
  ],
}

function formatValue(value: number): string {
  if (value >= 1e12) {
    return `${(value / 1e12).toFixed(1)}T`
  }
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(1)}B`
  }
  if (value >= 1e6) {
    return `${(value / 1e6).toFixed(1)}M`
  }
  if (value >= 1e3) {
    return `${(value / 1e3).toFixed(1)}K`
  }
  return value.toLocaleString()
}

function formatChange(change: number): React.ReactNode {
  const percentage = Math.abs(change) * 100
  const colorClassName = change >= 0 ? "text-fg-emerald" : "text-fg-red"
  return (
    <span className={colorClassName}>
      {change >= 0 ? "+" : "-"}
      {percentage.toFixed(1)}%
    </span>
  )
}

export function MasonryFinancialStatement() {
  return (
    <MasonryWidget
      href={PRODUCTS.EXPLORER_ROUTE}
      icon={Binoculars}
      productName="Explorer"
      widgetName="Financial statements"
      description="Analyze income statements with fees, revenue, expenses, and earnings for blockchain projects using traditional financial reporting formats."
      height={500}
      featuredVisual={
        <div
          className="relative bg-bg-surface border-t border-l border-solid border-border-default rounded-tl-xl flex-1 min-h-0"
          style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
        >
          <div className="overflow-x-hidden overflow-y-hidden relative w-full">
            <div className="w-full overflow-x-auto  relative">
              <Table
                rowStyle="no-border"
                rowBorderRadius="none"
                className="financial-statement-table w-full"
                density="large"
                style={
                  {
                    "--cell-font-size": "12px",
                    "--cell-padding-x": "8px",
                  } as React.CSSProperties
                }
              >
                <TableHeader>
                  <Row>
                    <HeaderCell className="min-w-[180px] sticky left-0 z-10 bg-bg-surface border-b border-solid border-border-default">
                      <div className="flex items-center gap-1.5">
                        <Image
                          src={`${BASE_PATH}/static/images/marketing/logos/ethereum.png`}
                          alt={mockFinancialData.project.name}
                          width={14}
                          height={14}
                          className="w-3.5 h-3.5 rounded-full object-cover"
                        />
                        <span className="text-sm text-fg-default">
                          {mockFinancialData.project.name}
                        </span>
                      </div>
                    </HeaderCell>
                    {mockFinancialData.periods.map((period) => (
                      <HeaderCell
                        key={period}
                        className="border-b border-solid border-border-default"
                        style={{ minWidth: "120px" }}
                      >
                        <div className="flex flex-col gap-0 leading-none items-end">
                          <div className="text-sm font-medium">{period}</div>
                        </div>
                      </HeaderCell>
                    ))}
                  </Row>
                </TableHeader>
                <TableBody>
                  {mockFinancialData.sections.map((section, sectionIndex) => (
                    <Fragment key={section.name}>
                      {sectionIndex > 0 && (
                        <Row>
                          <Cell className="px-0 pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                            <div className="h-px border-t border-solid border-border-default" />
                          </Cell>
                          <Cell
                            className="px-0 pt-3.5 pb-1"
                            colSpan={mockFinancialData.periods.length}
                          >
                            <div className="h-px border-t border-solid border-border-default" />
                          </Cell>
                        </Row>
                      )}

                      <Row>
                        <Cell className="font-medium pt-3.5 pb-1 sticky left-0 z-10 bg-bg-surface">
                          {section.name}
                        </Cell>
                        {mockFinancialData.periods.map((period) => (
                          <Cell key={period} className="pt-0 pb-1" />
                        ))}
                      </Row>

                      {section.metrics.map((metric) => (
                        <Row key={metric.id} className="group/metric-row">
                          <Cell
                            className={`py-1! align-top sticky left-0 z-10 bg-bg-surface group-hover/metric-row:bg-(--row-hover-color) ${
                              metric.isEarnings
                                ? "border-t border-solid border-border-default"
                                : ""
                            }`}
                          >
                            <div
                              className={`${
                                metric.isEarnings
                                  ? "text-fg-default"
                                  : "text-fg-secondary"
                              } ${metric.isExpense ? "ml-3" : ""}`}
                            >
                              {metric.label}
                            </div>
                          </Cell>
                          {metric.values.map((value, i) => (
                            <Cell
                              key={i}
                              className={`py-1! align-top group-hover/metric-row:bg-(--row-hover-color) ${
                                metric.isEarnings
                                  ? "border-t border-solid border-border-default"
                                  : ""
                              }`}
                            >
                              <div
                                className="flex items-center justify-end gap-px text-right whitespace-nowrap"
                                style={{
                                  fontFamily: "var(--font-geist-mono)",
                                }}
                              >
                                <div className="flex items-center gap-0.5 whitespace-nowrap">
                                  <span className="text-fg-secondary/80">
                                    $
                                  </span>
                                  <span>{formatValue(value)}</span>
                                </div>
                                <div className="min-w-[40px] ml-1 whitespace-nowrap">
                                  {formatChange(metric.changes[i])}
                                </div>
                              </div>
                            </Cell>
                          ))}
                        </Row>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      }
    />
  )
}
